/*
 * Copyright (c) 2025 curryfirm.com
 */

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    Button,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    Alert,
    List,
    ListItem,
} from '@mui/material';
import {
    fetchDumpstersThunk,
    selectAllDumpsters,
    selectDumpstersAreLoading,
    selectDumpsterError,
    selectDumpstersAtLot,
} from '../../../features/dumpsters/dumpstersSlice.js';
import {
    selectEmptyAvailableCans,
    setEmptyAvailableCans,
    selectReturnToSummaryAvailable,
    setReturnToSummaryAvailable,
    setStep,
    WIZARD_STEPS, setLocationChoosingMode, setLocationDropLot,
} from '../../../features/workflow/workflowSlice.js';
import { BACK, FORWARD, LOCATION_CHOOSING_MODES, } from '../../../constants.js';

const ChooseEmptyCan = ({ onBack, onContinue }) => {
    const dispatch = useDispatch();

    // Selectors
    const storedChoice = useSelector(selectEmptyAvailableCans);
    const dumpsters = useSelector(selectAllDumpsters);
    const dumpstersAtLot = useSelector(selectDumpstersAtLot);
    const loading = useSelector(selectDumpstersAreLoading);
    const error = useSelector(selectDumpsterError);
    const returnToSummaryAvailable = useSelector(selectReturnToSummaryAvailable);

    // Local state: one selected dumpster ID; none initially
    const [chosenId, setChosenId] = useState(
        storedChoice.length > 0 ? storedChoice[0] : ''
    );

    // Load inventory if required
    useEffect(() => {
        if (dumpsters.length === 0 && !loading) {
            dispatch(fetchDumpstersThunk());
        }
    }, [dispatch, dumpsters.length, loading]);

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }
    if (loading) {
        return <Typography variant="body1">Loading dumpsters...</Typography>;
    }

    // Define a function to store the empty's ID and the starting point conditionally.
    // That is, save the ID of the chosen empty in the store if it has changed.
    // If so, also set the starting point of the route, in terms of
    // location choosing mode drop-lot and location key (i.e. street|locationId).
    const storeEmptyAndStartingLocationIfChoiceChanged = () => {
        if (chosenId && storedChoice[0] !== chosenId) {
            dispatch(setEmptyAvailableCans([chosenId]));
            const emptyDetails = dumpstersAtLot.find(d => d.dumpsterId === chosenId);
            const locationKey = emptyDetails.street + '|' + emptyDetails.locationId;
            dispatch(setLocationChoosingMode({ field: 'startLocationChoosingMode', value: LOCATION_CHOOSING_MODES.dropLot }));
            dispatch(setLocationDropLot({ field: 'startLocationDropLot', value: locationKey }));
        }
    };

    const handleLeaveStep = (direction) => {
        storeEmptyAndStartingLocationIfChoiceChanged();
        direction === FORWARD ? onContinue() : onBack();
    };

    const handleReturnToSummary = () => {
        storeEmptyAndStartingLocationIfChoiceChanged();
        dispatch(setStep(WIZARD_STEPS.SUMMARY));
        dispatch(setReturnToSummaryAvailable(false));
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Choose an Empty Dumpster for Swapping
            </Typography>

            {dumpstersAtLot.length < 1 ? (
                <Alert severity="info">No cans at lot. Click Next to continue.</Alert>
            ) : (
                <RadioGroup
                    name="empty-can-choice"
                    value={chosenId}
                    onChange={(e) => setChosenId(e.target.value)}
                >
                    <List>
                        {dumpstersAtLot.map((dumpster) => (
                            <ListItem key={dumpster.dumpsterId}>
                                <FormControlLabel
                                    value={dumpster.dumpsterId}
                                    control={<Radio color="primary" />}
                                    label={`${dumpster.dumpsterId}: ${dumpster.street}`}
                                />
                            </ListItem>
                        ))}
                    </List>
                </RadioGroup>
            )}

            <Box sx={{ mt: 3 }}>
                <Button variant="contained" onClick={() => handleLeaveStep(BACK)}>
                    Back
                </Button>
                <Button
                    variant="contained"
                    sx={{ ml: 2 }}
                    onClick={() => handleLeaveStep(FORWARD)}
                    disabled={!chosenId}
                >
                    Next
                </Button>

                {returnToSummaryAvailable && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleReturnToSummary}
                        sx={{ ml: 2 }}
                    >
                        Return to Summary
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default ChooseEmptyCan;
